package com.xorrus.trading.lot.remote.connect

import com.xorrus.trading.lot.relayclient.browser.ControllerService
import com.xorrus.trading.lot.relayclient.browser.ControllerService.Desktop
import com.xorrus.trading.lot.relayclient.browser.DesktopDirectory
import com.xorrus.trading.lot.relayclient.browser.I18n
import com.xorrus.trading.lot.relayclient.browser.Lang
import com.xorrus.trading.lot.relayclient.browser.RemoteNav
import com.xorrus.trading.lot.relay.engine.CloseReason
import com.xorrus.trading.lot.relayclient.facade.LotCommandException
import com.xorrus.trading.lot.relayclient.model.DesktopStatus
import io.kvision.Application
import io.kvision.BootstrapCssModule
import io.kvision.BootstrapModule
import io.kvision.CoreModule
import io.kvision.FontAwesomeModule
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.text.TextInput
import io.kvision.html.ButtonStyle
import io.kvision.html.button
import io.kvision.html.customTag
import io.kvision.html.div
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.panel.root
import io.kvision.startApplication
import io.kvision.state.ObservableValue
import io.kvision.state.bind
import io.kvision.utils.px
import io.kvision.utils.useModule
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.seconds

/**
 * LOT-Connect-SPA („Haustür" auf `/`): Pairing per QR-Scan/SAS und die Liste der
 * gekoppelten Desktops. Ein Gerät kann mit MEHREREN Desktops gekoppelt sein
 * (Relay-Lib ≥ 0.5.0, ein Pairing je hostId); die Strategie-SPA steuert immer genau
 * einen davon.
 *
 * - Genau ein Desktop und kein `?reason=` → direkt die Warteseite wie bisher: erst wenn
 *   der Desktop LIVE antwortet und seinen Update-Channel meldet (`desktop.status`,
 *   [RemoteNav.KNOWN_CHANNELS]), geht es per `location.replace` auf `/<channel>/?d=<id>`.
 * - Mehrere Desktops (oder `?reason=switch` aus der Strategie-SPA) → Desktop-Liste mit
 *   Name, Online-Status und Channel; ein Tap wählt den Desktop und leitet weiter.
 * - Den Namen vergibt der Nutzer HIER: direkt nach dem Koppeln und später per Stift in der
 *   Liste ([com.xorrus.trading.lot.relayclient.browser.DesktopDirectory], nur dieser Browser).
 *   Der Desktop kennt den Namen nicht — das funktioniert mit jedem Desktop-Stand.
 * - Rücksprünge aus der Strategie-SPA tragen `/?reason=…&d=…` — bei `disconnected`/
 *   `channel-changed` landet der betroffene Desktop direkt auf seiner Warteseite.
 */
/** Gemeinsames Theme der Remote-SPAs (Farben/Schrift der Desktop-App), Quelle: `remote-theme/`. */
@JsModule("/kotlin/css/lot-theme.css")
@JsNonModule
external val lotThemeCss: dynamic

sealed interface Screen {
    data object Checking : Screen

    /** QR-Scan; [canGoBack] = aus der Liste heraus („Desktop hinzufügen") statt Erstzustand. */
    data class Scan(val canGoBack: Boolean) : Screen

    /** SAS anzeigen, während der Desktop die Freigabe bestätigt (Protokoll-§7.2). */
    data class Sas(val code: String) : Screen

    /**
     * Namen für einen Desktop vergeben: nach dem Koppeln ([fromList] = false, danach Warteseite)
     * oder aus der Liste heraus (danach zurück zur Liste). [initial] ist Vorschlag bzw. bisheriger Name.
     */
    data class Name(val desktopId: String, val initial: String, val fromList: Boolean) : Screen

    data object DesktopList : Screen

    /** Warten auf Live-Kontakt zu GENAU diesem Desktop; [hint] erklärt, woran es gerade hängt. */
    data class Waiting(val desktopId: String, val hint: String? = null) : Screen
    data class Failure(val message: String) : Screen
}

/** Erreichbarkeit eines Desktops aus Sicht der Liste. */
enum class Probe { UNKNOWN, ONLINE, OFFLINE, REVOKED }

data class DesktopRow(
    val id: String,
    val name: String,
    val channel: String?,
    val state: Probe,
    /** Hinweis bei erreichbarem, aber unbrauchbarem Desktop (alt, ohne Channel). */
    val hint: String? = null,
)

class App : Application() {

    private val screen = ObservableValue<Screen>(Screen.Checking)
    private val scanHint = ObservableValue<String?>(null)
    private val rows = ObservableValue<List<DesktopRow>>(emptyList())

    /** Desktop, dessen Entkopplung gerade bestätigt werden soll; null = kein Overlay. */
    private val confirmUnpair = ObservableValue<String?>(null)

    /** Erklärtext zum `?reason=…`-Rücksprung; einmalig beim Start ausgewertet. */
    private val reasonBanner: String? = when (RemoteNav.reason()) {
        RemoteNav.REASON_UNPAIRED -> S.reasonUnpaired
        RemoteNav.REASON_DISCONNECTED -> S.reasonDisconnected
        RemoteNav.REASON_CHANNEL_CHANGED -> S.reasonChannelChanged
        RemoteNav.REASON_UNKNOWN_DESKTOP -> S.reasonUnknownDesktop
        else -> null
    }

    private var listLoop: Job? = null
    private var waitLoop: Job? = null

    init {
        useModule(lotThemeCss)
    }

    override fun start() {
        root("kvapp") {
            div(className = "d-flex flex-column min-vh-100 p-3 mx-auto w-100") {
                maxWidth = 480.px

                div(className = "text-center mb-4 mt-2") {
                    span("LOT Remote Control", className = "fs-3 fw-bold d-block lot-brand")
                    span(S.appSubtitle, className = "text-secondary small")
                }

                reasonBanner?.let { div(it, className = "alert alert-info small") }

                div(className = "flex-grow-1 d-flex flex-column").bind(screen) { current ->
                    when (current) {
                        Screen.Checking -> renderSpinner(S.checkingPairing)
                        is Screen.Scan -> renderScan(current)
                        is Screen.Sas -> renderSas(current)
                        is Screen.Name -> renderName(current)
                        Screen.DesktopList -> renderDesktopList()
                        is Screen.Waiting -> renderWaiting(current)
                        is Screen.Failure -> renderFailure(current)
                    }
                }

                // Sprachumschalter als Button-Gruppe — immer sichtbar, unabhängig vom Screen.
                div(className = "text-center mt-4") {
                    div(className = "btn-group btn-group-sm") {
                        setAttribute("role", "group")
                        languageButton(Lang.DE)
                        languageButton(Lang.EN)
                    }
                }
            }

            // Bestätigungs-Overlay fürs Entkoppeln (Warteseite und Liste)
            div().bind(confirmUnpair) { id ->
                if (id != null) renderConfirmUnpair(id)
            }
        }
        bootstrap()
    }

    // ---- Screens -----------------------------------------------------------------

    /** Ein Knopf des DE/EN-Umschalters; die aktive Sprache ist als gewählt markiert. */
    private fun Container.languageButton(target: Lang) {
        val active = I18n.lang == target
        button(target.name, style = ButtonStyle.OUTLINESECONDARY, className = if (active) "active" else null) {
            if (active) setAttribute("aria-pressed", "true")
            onClick { I18n.switchTo(target) }
        }
    }

    private fun Container.renderSpinner(text: String) {
        div(className = "text-center my-auto") {
            div(className = "spinner-border text-primary mb-3") { setAttribute("role", "status") }
            p(text, className = "text-secondary")
        }
    }

    private fun Container.renderScan(scan: Screen.Scan) {
        p(
            S.scanInstruction,
            rich = true,
            className = "text-center text-secondary",
        )
        add(QrScannerPanel(onDecoded = ::handlePairUri, onError = { scanHint.value = it }))

        div().bind(scanHint) { hint ->
            if (hint != null) div(hint, className = "alert alert-warning mt-3 small")
        }

        div(className = "mt-3") {
            val input = TextInput(className = "form-control mb-2") {
                placeholder = S.manualPastePlaceholder
            }
            add(input)
            button(S.pairManuallyButton, style = ButtonStyle.SECONDARY, className = "w-100") {
                onClick { handlePairUri(input.value.orEmpty()) }
            }
        }
        if (scan.canGoBack) {
            div(className = "mt-auto pt-3") {
                button(S.backToListButton, style = ButtonStyle.OUTLINESECONDARY, className = "w-100") {
                    onClick { showList() }
                }
            }
        }
    }

    private fun Container.renderSas(sas: Screen.Sas) {
        div(className = "text-center my-auto") {
            p(S.sasTitle, className = "text-secondary mb-1")
            span(sas.code, className = "display-4 fw-bold d-block mb-3 lot-sas")
            p(
                S.sasConfirmHint,
                className = "text-secondary",
            )
            div(className = "spinner-border text-primary mt-3") { setAttribute("role", "status") }
        }
    }

    private fun Container.renderName(name: Screen.Name) {
        div(className = "my-auto") {
            p(S.nameTitle, className = "fw-bold text-center mb-1")
            p(S.nameHint, className = "text-secondary small text-center")
            val input = TextInput(value = name.initial, className = "form-control mb-3") {
                placeholder = S.namePlaceholder
                maxlength = DesktopDirectory.MAX_NAME_LENGTH
            }
            add(input)
            val proceed = { if (name.fromList) showList() else startWaiting(name.desktopId) }
            div(className = "d-flex gap-2") {
                button(S.saveButton, style = ButtonStyle.PRIMARY, className = "flex-fill") {
                    onClick {
                        ControllerService.renameDesktop(name.desktopId, input.value.orEmpty())
                        proceed()
                    }
                }
                // Ohne Namen weiter: die Liste zeigt dann einen Platzhalter, der Stift benennt später.
                button(if (name.fromList) S.cancelButton else S.skipButton, style = ButtonStyle.OUTLINESECONDARY, className = "flex-fill") {
                    onClick { proceed() }
                }
            }
        }
    }

    private fun Container.renderDesktopList() {
        p(S.desktopListTitle, className = "text-secondary text-center")
        div(className = "mb-3").bind(rows) { list ->
            list.forEach { row -> renderDesktopRow(row) }
        }
        div(className = "mt-auto pt-3") {
            button(S.addDesktopButton, style = ButtonStyle.PRIMARY, className = "w-100") {
                onClick { showScan(canGoBack = true) }
            }
        }
    }

    private fun Container.renderDesktopRow(row: DesktopRow) {
        val (statusText, statusClass) = when (row.state) {
            Probe.UNKNOWN -> "…" to "lot-status-idle"
            Probe.ONLINE -> S.stateOnline to "lot-status-ok"
            Probe.OFFLINE -> S.stateOffline to "lot-status-bad"
            Probe.REVOKED -> S.stateRevoked to "lot-status-warn"
        }
        // Hauptaktion (Desktop öffnen) und Nebenaktionen (umbenennen, entkoppeln) sind getrennte
        // Buttons nebeneinander — keine Buttons IN einer klickbaren Zeile. Der Pfeil zeigt: führt weiter.
        div(className = "d-flex align-items-stretch gap-2 mb-2") {
            customTag("button", className = "lot-action flex-grow-1") {
                setAttribute("type", "button")
                onClick { onDesktopTapped(row) }
                div(className = "flex-grow-1") {
                    span(row.name, className = "fw-bold d-block")
                    val detail = listOfNotNull(row.channel?.let { S.channelLabel(it) }, row.hint).joinToString(" · ")
                    if (detail.isNotEmpty()) span(detail, className = "text-secondary small d-block")
                }
                span(statusText, className = "lot-status $statusClass")
                span(className = "fas fa-chevron-right lot-chevron") { setAttribute("aria-hidden", "true") }
            }
            button("", icon = "fas fa-pen", style = ButtonStyle.OUTLINESECONDARY) {
                title = S.renameButton
                setAttribute("aria-label", S.renameButton)
                onClick {
                    stopLoops()
                    screen.value = Screen.Name(row.id, ControllerService.desktopName(row.id).orEmpty(), fromList = true)
                }
            }
            button("", icon = "fas fa-unlink", style = ButtonStyle.OUTLINEDANGER) {
                title = S.unpairButton
                setAttribute("aria-label", S.unpairButton)
                onClick { confirmUnpair.value = row.id }
            }
        }
    }

    private fun Container.renderWaiting(waiting: Screen.Waiting) {
        div(className = "text-center my-auto") {
            div(className = "spinner-border text-primary mb-3") { setAttribute("role", "status") }
            p(S.waitingTitle(ControllerService.desktopDisplayName(waiting.desktopId)), className = "fw-bold mb-1")
            p(
                waiting.hint ?: S.waitingDefaultHint,
                className = "text-secondary small",
            )
        }
        // Auswege: andere Desktops / neuen koppeln — oder diese Kopplung lokal lösen,
        // wenn sie nicht mehr gültig ist (z. B. am Desktop entfernt).
        div(className = "mt-auto pt-3 d-flex flex-column gap-2") {
            button(S.desktopListButton, style = ButtonStyle.OUTLINESECONDARY, className = "w-100") {
                onClick { showList() }
            }
            button(S.unpairButton, style = ButtonStyle.OUTLINEDANGER, className = "w-100") {
                onClick { confirmUnpair.value = waiting.desktopId }
            }
        }
    }

    private fun Container.renderConfirmUnpair(id: String) {
        div(className = "position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75 p-3") {
            setAttribute("style", "z-index: 1050;")
            div(className = "card w-100") {
                maxWidth = 380.px
                div(className = "card-body") {
                    p(S.unpairConfirmQuestion(ControllerService.desktopDisplayName(id)))
                    div(className = "d-flex gap-2") {
                        button(S.unpairConfirmButton, style = ButtonStyle.DANGER, className = "flex-fill") {
                            onClick {
                                confirmUnpair.value = null
                                unpair(id)
                            }
                        }
                        button(S.cancelButton, style = ButtonStyle.SECONDARY, className = "flex-fill") {
                            onClick { confirmUnpair.value = null }
                        }
                    }
                }
            }
        }
    }

    private fun Container.renderFailure(failure: Screen.Failure) {
        div(className = "my-auto") {
            div(failure.message, className = "alert alert-danger")
            button(S.retryButton, style = ButtonStyle.PRIMARY, className = "w-100") {
                onClick {
                    scanHint.value = null
                    screen.value = Screen.Checking
                    bootstrap()
                }
            }
        }
    }

    // ---- Ablauf ------------------------------------------------------------------

    private fun bootstrap() {
        ControllerService.scope.launch {
            try {
                val desktops = ControllerService.desktops()
                val requested = RemoteNav.desktopParam()?.takeIf { id -> desktops.any { it.id == id } }
                val reason = RemoteNav.reason()
                when {
                    desktops.isEmpty() -> showScan(canGoBack = false)
                    // Rücksprung wegen Verbindungsverlust/Channel-Wechsel: der betroffene
                    // Desktop wartet direkt auf Live-Kontakt (heutiges Verhalten)
                    requested != null && (reason == RemoteNav.REASON_DISCONNECTED || reason == RemoteNav.REASON_CHANNEL_CHANGED) ->
                        startWaiting(requested)
                    // Ein Desktop, kein besonderer Anlass: wie bisher ohne Umweg weiter
                    desktops.size == 1 && reason == null -> startWaiting(desktops.single().id)
                    else -> showList()
                }
            } catch (e: Exception) {
                screen.value = Screen.Failure(S.startFailed(e.message))
            }
        }
    }

    private fun showScan(canGoBack: Boolean) {
        stopLoops()
        scanHint.value = null
        screen.value = Screen.Scan(canGoBack)
    }

    private fun handlePairUri(raw: String) {
        val uri = raw.trim()
        if (!uri.startsWith("lotpair:")) {
            scanHint.value = S.notAPairCode
            return
        }
        ControllerService.scope.launch {
            try {
                val desktop = ControllerService.pair(uri) { sas -> screen.value = Screen.Sas(sas) }
                // Erst benennen, dann warten: ein erneut gekoppelter Desktop behält seinen Namen
                // als Vorschlag, ein neuer bekommt „Desktop <n>".
                val initial = ControllerService.desktopName(desktop.id)
                    ?: S.defaultDesktopName(ControllerService.desktops().size)
                screen.value = Screen.Name(desktop.id, initial, fromList = false)
            } catch (e: Exception) {
                screen.value = Screen.Failure(S.pairingFailed(e.message))
            }
        }
    }

    /** Desktop-Liste mit paralleler Erreichbarkeits-Probe aller Desktops (5 s, solange einer unbestimmt ist, sonst 15 s). */
    private fun showList() {
        stopLoops()
        screen.value = Screen.DesktopList
        listLoop = ControllerService.scope.launch {
            while (true) {
                val desktops = ControllerService.desktops()
                if (desktops.isEmpty()) {
                    showScan(canGoBack = false)
                    return@launch
                }
                val known = rows.value.associateBy { it.id }
                rows.value = desktops.map { d -> known[d.id] ?: d.row(Probe.UNKNOWN) }.sortedBy { it.name.lowercase() }
                val probed = coroutineScope { desktops.map { d -> async { probe(d) } }.awaitAll() }
                rows.value = probed.sortedBy { it.name.lowercase() }
                delay(if (probed.any { it.state == Probe.UNKNOWN }) 5.seconds else 15.seconds)
            }
        }
    }

    private suspend fun probe(desktop: Desktop): DesktopRow {
        if (desktop.terminated == CloseReason.REVOKED) return desktop.row(Probe.REVOKED)
        return try {
            val status = desktop.lot.desktopStatus()
            ControllerService.rememberStatus(desktop.id, status)
            desktop.row(Probe.ONLINE, hint = status.unusableHint())
        } catch (e: LotCommandException) {
            if (e.code == LotCommandException.TIMEOUT || e.code == LotCommandException.SESSION_CLOSED) {
                desktop.row(Probe.OFFLINE)
            } else {
                // z. B. UNKNOWN_CMD eines älteren Desktops ohne desktop.status
                desktop.row(Probe.ONLINE, hint = S.desktopTooOld)
            }
        } catch (_: Exception) {
            desktop.row(Probe.OFFLINE)
        }
    }

    private fun Desktop.row(state: Probe, hint: String? = null) =
        DesktopRow(id = id, name = displayName, channel = info.channel, state = state, hint = hint)

    private fun onDesktopTapped(row: DesktopRow) {
        val channel = row.channel
        if (row.state == Probe.ONLINE && channel in RemoteNav.KNOWN_CHANNELS) {
            ControllerService.scope.launch {
                ControllerService.select(row.id)
                RemoteNav.toStrategySpa(channel!!, row.id)
            }
        } else {
            startWaiting(row.id)
        }
    }

    /**
     * Warteseite + Poll-Schleife für EINEN Desktop: `desktop.status` fragen, bis er antwortet
     * UND einen bekannten Channel meldet — dann weiterleiten. Kein Timeout: Der Nutzer
     * sieht, worauf gewartet wird, und die Weiterleitung passiert bewusst erst nach
     * Live-Kontakt (nie aus einem Cache).
     */
    private fun startWaiting(desktopId: String) {
        stopLoops()
        screen.value = Screen.Waiting(desktopId)
        waitLoop = ControllerService.scope.launch {
            val desktop = ControllerService.select(desktopId)
            if (desktop == null) {
                showList()
                return@launch
            }
            try {
                ControllerService.connect()
            } catch (_: Exception) {
                // offline — die Schleife unten versucht es weiter (ManagedClient verbindet lazy neu)
            }
            while (true) {
                val hint: String? = try {
                    val status = desktop.lot.desktopStatus()
                    ControllerService.rememberStatus(desktop.id, status)
                    console.log("Desktop-App ${status.version ?: "unbekannt"} (Channel ${status.channel ?: "unbekannt"})")
                    when (status.channel) {
                        in RemoteNav.KNOWN_CHANNELS -> {
                            RemoteNav.toStrategySpa(status.channel!!, desktop.id)
                            return@launch
                        }
                        else -> status.unusableHint()
                    }
                } catch (e: LotCommandException) {
                    if (e.code == LotCommandException.TIMEOUT || e.code == LotCommandException.SESSION_CLOSED) {
                        if (desktop.terminated == CloseReason.REVOKED) S.desktopRevoked else S.desktopOffline
                    } else {
                        // z. B. UNKNOWN_CMD eines älteren Desktops ohne desktop.status
                        S.desktopTooOld
                    }
                } catch (_: Exception) {
                    S.desktopOffline
                }
                screen.value = Screen.Waiting(desktop.id, hint)
                delay(5.seconds)
            }
        }
    }

    /**
     * Erreichbar, aber (noch) nicht steuerbar: älterer Desktop ohne Channel-Meldung
     * (heilt sich mit dem nächsten Neustart, Update4J zieht den Channel-Head) oder
     * unbekannter Channel. Null = alles gut.
     */
    private fun DesktopStatus.unusableHint(): String? = when (channel) {
        in RemoteNav.KNOWN_CHANNELS -> null
        null -> S.channelNotReported
        else -> S.unknownChannel(channel!!)
    }

    private fun unpair(id: String) {
        ControllerService.scope.launch {
            ControllerService.unpair(id)
            if (ControllerService.desktops().isEmpty()) {
                // Sauberer Neustart der SPA (räumt auch ?reason= weg) — landet ohne
                // Kopplung direkt auf dem QR-Scanner.
                window.location.replace("/")
            } else {
                showList()
            }
        }
    }

    private fun stopLoops() {
        listLoop?.cancel()
        listLoop = null
        waitLoop?.cancel()
        waitLoop = null
    }
}

fun main() {
    startApplication(::App, null, BootstrapModule, BootstrapCssModule, FontAwesomeModule, CoreModule)
}
